import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Stack, Button, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { shortAbsenceService } from '../../services/api';
import ShortAbsenceCalendar from './ShortAbsenceCalendar';
import ShortAbsenceRequestDialog from './ShortAbsenceRequestDialog';
import MyShortAbsences from './MyShortAbsences';
import ShortAbsenceStats from './ShortAbsenceStats';
import { useToast } from '../ToastProvider';
import { handleApiError } from '../Common/ApiErrorHandler';

export default function ShortAbsencesPage() {
  const [absences, setAbsences] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const toast = useToast();

  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [absData, statsData] = await Promise.all([
        shortAbsenceService.getMy(year, month),
        shortAbsenceService.getMyStats(year, month)
      ]);
      setAbsences(Array.isArray(absData) ? absData : []);
      setStats(statsData);
    } catch (e) {
      setError(handleApiError(e) || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSlotSelect = (date, startTime, endTime) => {
    setSelectedSlot({ date, startTime, endTime });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSlot(null);
  };

  const handleRequestCreated = (createdDate) => {
    handleDialogClose();
    // If the created request is in a different month, switch to that month
    if (createdDate) {
      const d = new Date(createdDate);
      const createdYear = d.getFullYear();
      const createdMonth = d.getMonth() + 1;
      if (createdYear !== year || createdMonth !== month) {
        setYear(createdYear);
        setMonth(createdMonth);
        // loadData will be triggered by useEffect when year/month change
        toast.success('Short absence request submitted - switched to ' + d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
        return;
      }
    }
    loadData();
    toast.success('Short absence request submitted');
  };

  const handleCancel = async (id) => {
    try {
      await shortAbsenceService.cancel(id);
      toast.success('Request cancelled');
      loadData();
    } catch (e) {
      handleApiError(e);
    }
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5">Short Absences</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            New Request
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2}>
          {/* Stats Card */}
          <Paper sx={{ p: 2 }}>
            <ShortAbsenceStats stats={stats} loading={loading} />
          </Paper>

          {/* Calendar */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Weekly Calendar</Typography>
            <ShortAbsenceCalendar
              absences={absences}
              onSlotSelect={handleSlotSelect}
            />
          </Paper>

          {/* My Requests List */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>My Requests</Typography>
            <MyShortAbsences
              absences={absences}
              loading={loading}
              onCancel={handleCancel}
            />
          </Paper>
        </Stack>
      </Box>

      <ShortAbsenceRequestDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onCreated={handleRequestCreated}
        initialSlot={selectedSlot}
        currentStats={stats}
      />
    </>
  );
}

